import { apiService, tokenManager } from './api';

// Secure Admin API Service
export const adminApiService = {
  // Ensure token is present for all admin operations
  _ensureAuthenticated: () => {
    const token = tokenManager.getToken();
    console.log('🔍 AdminAPI - Checking token:', { tokenExists: !!token, tokenLength: token?.length });
    if (!token) {
      console.error('❌ AdminAPI - No token found for API call');
      throw new Error('Authentication required - no token found');
    }
    return token;
  },

  // Admin Management APIs
  admins: {
    getAll: async () => {
      adminApiService._ensureAuthenticated();
      return await apiService.get('/api/v1/admins/datatable');
    },

    getById: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/admins/${id}`);
    },

    create: async (adminData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post('/api/v1/admins', adminData);
    },

    update: async (id, adminData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.put(`/api/v1/admins/${id}`, adminData);
    },

    delete: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.delete(`/api/v1/admins/${id}`);
    },

    updatePermissions: async (id, permissions) => {
      adminApiService._ensureAuthenticated();
      return await apiService.put(`/api/v1/admins/${id}/permissions`, permissions);
    }
  },

  // Event Management APIs
  events: {
    getAll: async (params = {}) => {
      adminApiService._ensureAuthenticated();
      const queryString = new URLSearchParams(params).toString();
      return await apiService.get(`/api/v1/events${queryString ? `?${queryString}` : ''}`);
    },

    getById: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/events/${id}`);
    },

    create: async (eventData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post('/api/v1/events', eventData);
    },

    update: async (id, eventData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.put(`/api/v1/events/${id}`, eventData);
    },

    delete: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.delete(`/api/v1/events/${id}`);
    },

    duplicate: async (eventId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post(`/api/v1/events/${eventId}/duplicate`);
    }
    ,

    // Course enrollments (per section)
    getEnrollments: async (eventId, sectionId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/events/${eventId}/sections/${sectionId}/enrollments`);
    },

    addEnrollment: async (eventId, sectionId, memberId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post(`/api/v1/events/${eventId}/sections/${sectionId}/enrollments`, {
        event_id: eventId,
        section_id: sectionId,
        member_id: memberId
      });
    },

    /**
     * Bulk-enroll all active members under a group / zone / district.
     * @param {'group'|'zone'|'district'} scope
     * @param {number} scopeId
     */
    bulkAddEnrollmentsByOrg: async (eventId, sectionId, scope, scopeId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post(
        `/api/v1/events/${eventId}/sections/${sectionId}/enrollments/bulk-by-org`,
        { scope, scope_id: scopeId }
      );
    },

    removeEnrollment: async (eventId, sectionId, memberId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.delete(`/api/v1/events/${eventId}/sections/${sectionId}/enrollments/${memberId}`);
    }
    ,
    updateEnrollmentStatus: async (eventId, sectionId, memberId, status) => {
      adminApiService._ensureAuthenticated();
      return await apiService.put(`/api/v1/events/${eventId}/sections/${sectionId}/enrollments/${memberId}/status`, { status });
    }
  },

  // Attendance Management APIs
  attendance: {
    getAll: async (params = {}) => {
      adminApiService._ensureAuthenticated();
      const queryString = new URLSearchParams({
        skip: params.skip || 0,
        limit: params.limit || 100,
        ...(params.event_id && { event_id: params.event_id }),
        ...(params.section_id && { section_id: params.section_id })
      }).toString();
      return await apiService.get(`/api/v1/attendance${queryString ? `?${queryString}` : ''}`);
    },

    getRecords: async (params = {}) => {
      adminApiService._ensureAuthenticated();
      const queryString = new URLSearchParams(params).toString();
      return await apiService.get(`/api/v1/attendance/datatable${queryString ? `?${queryString}` : ''}`);
    },

    checkIn: async (checkInData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post('/api/v1/attendance/check_in', checkInData);
    },

    getByEventSection: async (eventId, sectionId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/attendance/event/${eventId}/section/${sectionId}`);
    },

    deleteRecord: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.delete(`/api/v1/attendance/${id}`);
    }
  },

  // Tithe Receipt Management APIs
  titheReceipts: {
    getAll: async (params = {}) => {
      adminApiService._ensureAuthenticated();
      const queryString = new URLSearchParams(params).toString();
      return await apiService.get(`/api/v1/tithe-receipts${queryString ? `?${queryString}` : ''}`);
    },

    getById: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/tithe-receipts/${id}`);
    },

    create: async (receiptData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post('/api/v1/tithe-receipts', receiptData);
    },

    update: async (id, receiptData) => {
      adminApiService._ensureAuthenticated();
      return await apiService.put(`/api/v1/tithe-receipts/${id}`, receiptData);
    },

    delete: async (id) => {
      adminApiService._ensureAuthenticated();
      return await apiService.delete(`/api/v1/tithe-receipts/${id}`);
    },

    uploadReceipt: async (file, metadata = {}) => {
      adminApiService._ensureAuthenticated();
      return await apiService.uploadFile('/api/v1/tithe-receipts/upload', file);
    },

    getStatistics: async () => {
      adminApiService._ensureAuthenticated();
      return await apiService.get('/api/v1/tithe-receipts/statistics/summary');
    },

    downloadReceipt: async (receiptId) => {
      adminApiService._ensureAuthenticated();
      return await apiService.get(`/api/v1/tithe-receipts/${receiptId}/download-receipt`, {
        responseType: 'blob'
      });
    }
  },

  // Dashboard/Statistics APIs
  dashboard: {
    getStats: async () => {
      adminApiService._ensureAuthenticated();
      return await apiService.get('/api/v1/dashboard/stats');
    },

    getRecentActivity: async () => {
      adminApiService._ensureAuthenticated();
      return await apiService.get('/api/v1/dashboard/recent-activity');
    }
  },

  // System / Settings APIs
  system: {
    clearAllData: async (categories) => {
      adminApiService._ensureAuthenticated();
      return await apiService.post('/api/v1/admin/clear-data', {
        confirm: true,
        categories,
      });
    }
  }
};

// Export default for backward compatibility
export default adminApiService;
